import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Columns2,
  Download,
  ExternalLink,
  MapPin,
  MousePointerClick,
  RotateCcw,
  Send,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import type { BannerDetail, Comment, Decision, PlatformResult, VersionDetail } from '../../shared/types';
import { api, ApiError, del, patch, post, qk, uploadFiles, withShare } from '../lib/api';
import { ACTOR_LABEL } from '../lib/activity';
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatName,
  formatTimecode,
  KIND_LABELS,
  relativeTime,
} from '../lib/format';
import { useTitle } from '../components/AppShell';
import { BannerFrame, sizeOf, useElementSize, type FrameEvent } from '../components/BannerMedia';
import { useGuest } from '../components/GuestProvider';
import { errorMessage, useToast } from '../components/toast';
import { CheckIcon, Dialog, Notice, Spinner, StatusChip, Tabs } from '../components/ui';

type Bg = 'light' | 'grey' | 'dark' | 'checker';
const BG_LABEL: Record<Bg, string> = { light: 'Weiß', grey: 'Grau', dark: 'Dunkel', checker: 'Raster' };

function loadBg(): Bg {
  try {
    const v = localStorage.getItem('zeigbanner.stageBg');
    return v === 'light' || v === 'grey' || v === 'dark' || v === 'checker' ? v : 'grey';
  } catch {
    return 'grey';
  }
}

interface Pin {
  x: number;
  y: number;
}

// ---------- Bühne ----------

function StageColumn({
  version,
  scale,
  replayKey,
  label,
  pins,
  draft,
  pinMode,
  highlight,
  onPlacePin,
  onPinClick,
  onEvent,
  onVideo,
}: {
  version: VersionDetail;
  scale: number;
  replayKey: number;
  label?: string;
  pins?: { id: string; number: number; x: number; y: number; resolved: boolean }[];
  draft?: Pin | null;
  pinMode?: boolean;
  highlight?: string | null;
  onPlacePin?: (pin: Pin) => void;
  onPinClick?: (id: string) => void;
  onEvent?: (e: FrameEvent) => void;
  onVideo?: (el: HTMLVideoElement | null) => void;
}) {
  const { w, h } = sizeOf(version);
  const overlay = onPlacePin ? (
    <div
      className={`pin-layer ${pinMode ? 'is-active' : ''}`}
      onClick={(e) => {
        if (!pinMode) return;
        const rect = e.currentTarget.getBoundingClientRect();
        onPlacePin({
          x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
          y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
        });
      }}
    >
      {pins?.map((pin) => (
        <button
          key={pin.id}
          type="button"
          className={`pin ${pin.resolved ? 'is-resolved' : ''} ${highlight === pin.id ? 'is-highlight' : ''}`}
          style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
          onClick={(e) => {
            e.stopPropagation();
            onPinClick?.(pin.id);
          }}
          aria-label={`Kommentar ${pin.number}`}
        >
          {pin.number}
        </button>
      ))}
      {draft && (
        <span className="pin is-draft" style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%` }} />
      )}
    </div>
  ) : null;
  return (
    <div className="stage-col">
      <BannerFrame
        version={version}
        scale={scale}
        replayKey={replayKey}
        overlay={overlay}
        onEvent={onEvent}
        onVideo={onVideo}
      />
      <div className="stage-caption">
        {label && <strong>{label}</strong>}
        <span>
          {w} × {h} px · {Math.round(scale * 100)} %
        </span>
      </div>
    </div>
  );
}

// ---------- Feedback ----------

function CommentCard({
  comment,
  number,
  replies,
  canReply,
  canModerate,
  highlight,
  showVersion,
  onFocus,
  onReply,
  onResolve,
  onDelete,
}: {
  comment: Comment;
  number?: number;
  replies: Comment[];
  canReply: boolean;
  canModerate: boolean;
  highlight: boolean;
  showVersion: boolean;
  onFocus: () => void;
  onReply: (parent: Comment, body: string) => Promise<void>;
  onResolve: (comment: Comment, resolved: boolean) => void;
  onDelete: (comment: Comment) => void;
}) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlight]);
  const renderOne = (c: Comment, isReply: boolean) => (
    <div
      key={c.id}
      className={`comment ${c.internal ? 'is-internal' : ''} ${c.resolvedAt && !isReply ? 'is-resolved' : ''} ${highlight && !isReply ? 'is-highlight' : ''}`}
    >
      <div className="comment-head">
        {!isReply && number !== undefined && <span className="pin-num">{number}</span>}
        <strong>{c.authorName}</strong>
        <span className="chip chip-outline" style={{ height: 20, fontSize: 10.5 }}>
          {ACTOR_LABEL[c.authorKind]}
        </span>
        {c.internal && !isReply && (
          <span className="chip chip-internal" style={{ height: 20, fontSize: 10.5 }}>
            intern
          </span>
        )}
        {showVersion && !isReply && <span className="tiny muted">v{c.versionNumber}</span>}
        {c.timeMs !== null && <span className="tiny muted">bei {formatTimecode(c.timeMs)}</span>}
        <span className="spacer" />
        <span className="tiny muted" title={formatDateTime(c.createdAt)}>
          {relativeTime(c.createdAt)}
        </span>
      </div>
      <div className="comment-body">{c.body}</div>
      {(canModerate || c.isOwn || (!isReply && canReply)) && (
        <div className="comment-actions">
          {!isReply && canReply && <button onClick={() => setReplying((r) => !r)}>Antworten</button>}
          {!isReply && (canModerate || c.isOwn) && (
            <button onClick={() => onResolve(c, !c.resolvedAt)}>
              {c.resolvedAt ? 'Wieder öffnen' : 'Erledigt'}
            </button>
          )}
          {(canModerate || c.isOwn) && <button onClick={() => onDelete(c)}>Löschen</button>}
        </div>
      )}
    </div>
  );
  return (
    <div ref={ref} className="stack" style={{ gap: 8 }} onMouseEnter={onFocus}>
      {renderOne(comment, false)}
      {(replies.length > 0 || replying) && (
        <div className="replies">
          {replies.map((r) => renderOne(r, true))}
          {replying && (
            <form
              className="stack"
              style={{ gap: 6 }}
              onSubmit={async (e) => {
                e.preventDefault();
                if (!text.trim()) return;
                await onReply(comment, text);
                setText('');
                setReplying(false);
              }}
            >
              <textarea
                className="textarea"
                style={{ minHeight: 60 }}
                value={text}
                autoFocus
                onChange={(e) => setText(e.target.value)}
                placeholder="Antwort …"
              />
              <div className="row">
                <button className="btn btn-primary btn-xs">Antworten</button>
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => setReplying(false)}>
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function DecisionEntry({ decision, showVersion }: { decision: Decision; showVersion: boolean }) {
  const label =
    decision.decision === 'approved'
      ? 'hat freigegeben'
      : decision.decision === 'changes_requested'
        ? 'wünscht Änderungen'
        : 'hat den Status zurückgesetzt';
  return (
    <div className={`decision-entry ${decision.decision}`}>
      {decision.decision === 'approved' ? (
        <Check size={17} strokeWidth={3} />
      ) : decision.decision === 'changes_requested' ? (
        <TriangleAlert size={17} />
      ) : (
        <RotateCcw size={16} />
      )}
      <div className="grow">
        <strong>{decision.actorName}</strong> {label}
        {showVersion && ` (v${decision.versionNumber})`}
        <span className="tiny" style={{ display: 'block', opacity: 0.75 }}>
          {ACTOR_LABEL[decision.actorKind]} · {formatDateTime(decision.createdAt)}
        </span>
        {decision.note && (
          <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', color: 'var(--ink)' }}>{decision.note}</div>
        )}
      </div>
    </div>
  );
}

// ---------- Technik ----------

function PlatformCard({ result, open }: { result: PlatformResult; open: boolean }) {
  const counts = result.checks.reduce(
    (acc, c) => ({ ...acc, [c.status]: (acc[c.status] ?? 0) + 1 }),
    {} as Record<string, number>,
  );
  return (
    <details className="platform" open={open}>
      <summary>
        <CheckIcon status={result.status} />
        <span className="grow">{result.name}</span>
        <span className="tiny muted">
          {counts.fail ? `${counts.fail} Fehler` : counts.warn ? `${counts.warn} Hinweise` : 'passt'}
        </span>
      </summary>
      <ul className="check-list">
        {result.checks.map((c) => (
          <li key={c.id}>
            <CheckIcon status={c.status} size={15} />
            <span>
              {c.label}
              {c.detail && <span className="detail">{c.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
      {result.source && (
        <p className="tiny" style={{ padding: '0 14px 12px' }}>
          <a className="text-link" href={result.source} target="_blank" rel="noreferrer">
            Spezifikation ansehen
          </a>
        </p>
      )}
    </details>
  );
}

function ChecksPanel({
  version,
  platforms,
  clicks,
}: {
  version: VersionDetail;
  platforms: string[];
  clicks: string[];
}) {
  const a = version.analysis;
  const selected = a.platforms.filter((p) => platforms.includes(p.platform));
  const others = a.platforms.filter((p) => !platforms.includes(p.platform));
  return (
    <div className="stack" style={{ gap: 18 }}>
      {version.kind === 'html5' && (
        <div className="stack" style={{ gap: 8 }}>
          <strong className="row">
            <MousePointerClick size={17} /> Klick-Test
          </strong>
          {a.clickTags?.length ? (
            a.clickTags.map((t) => (
              <div key={t.kind + t.name} className="small">
                <span className="mono">{t.name}</span>
                {t.url && (
                  <>
                    {' → '}
                    <a
                      className="text-link"
                      href={t.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {t.url}
                    </a>
                  </>
                )}
              </div>
            ))
          ) : (
            <p className="small muted">Im Code wurde kein Klickziel gefunden.</p>
          )}
          {clicks.length ? (
            clicks.map((url, i) => (
              <Notice key={i} kind="ok">
                Klick funktioniert: <span style={{ wordBreak: 'break-all' }}>{url || '(leere URL)'}</span>
              </Notice>
            ))
          ) : (
            <p className="small muted">Klicke ins Banner – das Klickziel erscheint dann hier.</p>
          )}
        </div>
      )}
      <div className="stack" style={{ gap: 8 }}>
        <strong>Allgemein</strong>
        <ul className="check-list" style={{ padding: 0 }}>
          {a.checks.map((c) => (
            <li key={c.id}>
              <CheckIcon status={c.status} size={15} />
              <span>
                {c.label}
                {c.detail && <span className="detail">{c.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {selected.length > 0 && (
        <div className="stack" style={{ gap: 0 }}>
          <strong style={{ marginBottom: 8 }}>Zielplattformen</strong>
          {selected.map((p) => (
            <PlatformCard key={p.platform} result={p} open={p.status !== 'pass'} />
          ))}
        </div>
      )}
      {others.length > 0 && (
        <details>
          <summary className="small" style={{ cursor: 'pointer', fontWeight: 600 }}>
            {selected.length ? 'Weitere Plattformen' : 'Plattformen'} ({others.length})
          </summary>
          <div style={{ marginTop: 10 }}>
            {others.map((p) => (
              <PlatformCard key={p.platform} result={p} open={false} />
            ))}
          </div>
        </details>
      )}
      <p className="tiny muted">
        Prüfung nach den veröffentlichten Plattform-Vorgaben (Stand 10/2026). Mediaplan oder Publisher können
        strengere Grenzen setzen.
      </p>
    </div>
  );
}

// ---------- Datei ----------

function FilePanel({
  version,
  banner,
  onDeleteVersion,
}: {
  version: VersionDetail;
  banner: BannerDetail;
  onDeleteVersion: () => void;
}) {
  const a = version.analysis;
  const rows: [string, string][] = [
    ['Datei', version.originalName],
    ['Typ', `${KIND_LABELS[version.kind]} · ${version.mimeType}`],
    [
      'Format',
      version.width && version.height
        ? `${version.width} × ${version.height} px · ${formatName(version.width, version.height)}`
        : 'unbekannt',
    ],
    ['Dateigröße', formatBytes(version.sizeBytes)],
  ];
  if (a.unzippedBytes !== undefined)
    rows.push(['Entpackt', `${formatBytes(a.unzippedBytes)} in ${a.fileCount} Dateien`]);
  if (a.entry) rows.push(['Startdatei', a.entry]);
  if (version.durationMs)
    rows.push([
      'Dauer',
      formatDuration(version.durationMs) + (a.frames ? ` · ${a.frames} Einzelbilder` : ''),
    ]);
  if (version.kind === 'gif' && a.frames && a.frames > 1) {
    rows.push([
      'Wiederholungen',
      a.loops === 0
        ? 'endlos'
        : a.loops
          ? `${a.loops}× (gesamt ${formatDuration(a.totalPlayMs)})`
          : 'spielt einmal',
    ]);
  }
  if (a.generator) rows.push(['Erstellt mit', a.generator]);
  rows.push([
    'Hochgeladen',
    `${formatDateTime(version.createdAt)}${version.uploadedByName ? ` von ${version.uploadedByName}` : ''}`,
  ]);
  if (version.note) rows.push(['Notiz', version.note]);
  return (
    <div className="stack" style={{ gap: 18 }}>
      <dl className="kv">
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {banner.permissions.canDownload && (
        <a className="btn btn-outline" href={withShare(`/api/versions/${version.id}/download`)}>
          <Download size={17} /> Originaldatei herunterladen
        </a>
      )}
      {a.files && a.files.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <strong>Inhalt des ZIP</strong>
          <div className="file-list">
            {a.files.map((f) => (
              <div key={f.path}>
                <span className="mono" style={{ wordBreak: 'break-all' }}>
                  {f.path}
                </span>
                <span className="muted nowrap">{formatBytes(f.size)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {banner.permissions.canManage && banner.versions.length > 1 && (
        <button
          className="btn btn-ghost btn-sm"
          style={{ alignSelf: 'flex-start', color: 'var(--danger-ink)' }}
          onClick={onDeleteVersion}
        >
          <Trash2 size={15} /> Version {version.number} löschen
        </button>
      )}
    </div>
  );
}

// ---------- Seite ----------

export function ReviewPage() {
  const { token, campaignId, bannerId = '' } = useParams();
  const base = token ? `/r/${token}` : `/kampagnen/${campaignId}`;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const ensureGuest = useGuest();

  const banner = useQuery({
    queryKey: qk('banner', bannerId),
    queryFn: () => api<BannerDetail>(`/api/banners/${bannerId}`),
  });
  const comments = useQuery({
    queryKey: qk('comments', bannerId),
    queryFn: () => api<Comment[]>(`/api/banners/${bannerId}/comments`),
  });
  useTitle(banner.data?.name);

  const [versionId, setVersionId] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);
  const [zoom, setZoom] = useState<'fit' | 'actual'>('fit');
  const [bg, setBg] = useState<Bg>(loadBg);
  const [replayKey, setReplayKey] = useState(0);
  const [pinMode, setPinMode] = useState(false);
  const [draft, setDraft] = useState<Pin | null>(null);
  const [timeMs, setTimeMs] = useState<number | null>(null);
  const [events, setEvents] = useState<FrameEvent[]>([]);
  const [tab, setTab] = useState<'feedback' | 'check' | 'file'>('feedback');
  const [scope, setScope] = useState<'version' | 'all'>('version');
  const [highlight, setHighlight] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesNote, setChangesNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const loadedAt = useRef(Date.now());
  const videoEl = useRef<HTMLVideoElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [stageRef, stageSize] = useElementSize<HTMLDivElement>();

  const data = banner.data;
  const current =
    data?.versions.find((v) => v.id === data.currentVersionId) ?? data?.versions[data.versions.length - 1];

  // Beim Wechsel des Motivs oder nach neuem Upload auf die aktuelle Version springen.
  useEffect(() => {
    setVersionId(null);
    setCompare(false);
    setDraft(null);
    setPinMode(false);
    setEvents([]);
    setHighlight(null);
  }, [bannerId, data?.currentVersionId]);

  // Aus der Kommentar-Übersicht (?kommentar=…): passende Version wählen und den Kommentar hervorheben.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('kommentar');
  useEffect(() => {
    if (!focusId || !data || !comments.data) return;
    const target = comments.data.find((c) => c.id === focusId);
    if (!target) return;
    const top = (target.parentId && comments.data.find((c) => c.id === target.parentId)) || target;
    setTab('feedback');
    setScope('version');
    setVersionId(top.versionId === data.currentVersionId ? null : top.versionId);
    setHighlight(top.id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('kommentar');
        return next;
      },
      { replace: true },
    );
  }, [focusId, data, comments.data, setSearchParams]);

  useEffect(() => {
    try {
      localStorage.setItem('zeigbanner.stageBg', bg);
    } catch {
      // egal
    }
  }, [bg]);

  const selected = data?.versions.find((v) => v.id === versionId) ?? current;
  const selectedIndex = selected && data ? data.versions.indexOf(selected) : -1;
  const previous = selectedIndex > 0 && data ? data.versions[selectedIndex - 1] : undefined;

  const replay = useCallback(() => {
    setReplayKey((k) => k + 1);
    setEvents([]);
    loadedAt.current = Date.now();
  }, []);

  useEffect(() => {
    loadedAt.current = Date.now();
  }, [selected?.id]);

  // Tastatur: ←/→ blättern, R = neu abspielen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable]') || e.metaKey || e.ctrlKey || e.altKey)
        return;
      if (!data) return;
      const i = data.siblings.findIndex((s) => s.id === data.id);
      if (e.key === 'ArrowLeft' && i > 0) navigate(`${base}/motiv/${data.siblings[i - 1].id}`);
      if (e.key === 'ArrowRight' && i < data.siblings.length - 1)
        navigate(`${base}/motiv/${data.siblings[i + 1].id}`);
      if (e.key === 'r' || e.key === 'R') replay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, base, navigate, replay]);

  const captureTime = () => {
    if (!selected) return;
    const elapsed = Date.now() - loadedAt.current;
    if (selected.kind === 'video' && videoEl.current) {
      videoEl.current.pause();
      setTimeMs(Math.round(videoEl.current.currentTime * 1000));
    } else if (selected.kind === 'gif' && selected.durationMs) {
      // Position im aktuellen Durchlauf – nur solange das GIF noch läuft.
      const total = selected.analysis.totalPlayMs;
      setTimeMs(
        total === null || (total !== undefined && elapsed < total) ? elapsed % selected.durationMs : null,
      );
    } else if (selected.kind === 'html5') {
      // Die meisten Banner-Animationen enden nach spätestens 30 Sekunden.
      setTimeMs(elapsed < 30_000 ? elapsed : null);
    }
  };

  const onFrameEvent = useCallback((event: FrameEvent) => {
    setEvents((list) => [...list.slice(-4), event]);
    if (event.type === 'click') setTab((t) => (t === 'file' ? 'check' : t));
  }, []);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: qk('comments', bannerId) });
    await queryClient.invalidateQueries({ queryKey: qk('banner', bannerId) });
    if (data) await queryClient.invalidateQueries({ queryKey: qk('campaign', data.campaign.id) });
  };

  const sendComment = useMutation({
    mutationFn: async (args: { body: string; parentId?: string }) => {
      const guest = await ensureGuest();
      if (data?.permissions.isGuest && !guest)
        throw new Error('Ohne Namen kann kein Kommentar gespeichert werden.');
      return post<Comment>(`/api/banners/${bannerId}/comments`, {
        versionId: selected!.id,
        body: args.body,
        parentId: args.parentId ?? null,
        internal: args.parentId ? false : internal,
        pinX: args.parentId ? null : (draft?.x ?? null),
        pinY: args.parentId ? null : (draft?.y ?? null),
        timeMs: args.parentId ? null : timeMs,
        guestName: guest?.name,
        guestEmail: guest?.email,
      });
    },
    onSuccess: async (comment, args) => {
      if (!args.parentId) {
        setText('');
        setDraft(null);
        setTimeMs(null);
        setPinMode(false);
        setHighlight(comment.id);
      }
      await invalidate();
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const decide = useMutation({
    mutationFn: async (args: { decision: 'approved' | 'changes_requested' | 'reset'; note?: string }) => {
      const guest = await ensureGuest();
      if (data?.permissions.isGuest && !guest) return null;
      return post(`/api/banners/${bannerId}/decision`, {
        versionId: current!.id,
        decision: args.decision,
        note: args.note ?? '',
        guestName: guest?.name,
        guestEmail: guest?.email,
      });
    },
    onSuccess: async (result, args) => {
      if (!result) return;
      setChangesOpen(false);
      setChangesNote('');
      toast(
        args.decision === 'approved'
          ? 'Freigegeben – danke!'
          : args.decision === 'reset'
            ? 'Status zurückgesetzt.'
            : 'Änderungswunsch gespeichert.',
      );
      await invalidate();
    },
    onError: async (err) => {
      toast(errorMessage(err), 'error');
      if (err instanceof ApiError && err.code === 'stale_version') await invalidate();
    },
  });

  const resolveComment = async (comment: Comment, resolved: boolean) => {
    try {
      await patch(`/api/comments/${comment.id}`, { resolved });
      await invalidate();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const deleteComment = async (comment: Comment) => {
    if (!window.confirm('Diesen Kommentar löschen?')) return;
    try {
      await del(`/api/comments/${comment.id}`);
      await invalidate();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const uploadVersion = async (file: File) => {
    if (!data) return;
    setUploading(true);
    try {
      const [result] = await uploadFiles(data.campaign.id, [file], { bannerId: data.id });
      if (!result?.ok) throw new Error(result?.error ?? 'Upload fehlgeschlagen');
      toast(`Version ${result.versionNumber} hochgeladen.`);
      await invalidate();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const deleteVersion = async () => {
    if (!selected || !window.confirm(`Version ${selected.number} endgültig löschen?`)) return;
    try {
      await del(`/api/versions/${selected.id}`);
      setVersionId(null);
      await invalidate();
      toast('Version gelöscht.');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  // Kommentare der gewählten Version mit Nummern für die Pins
  const thread = useMemo(() => {
    const all = comments.data ?? [];
    const top = all.filter((c) => !c.parentId && (scope === 'all' || c.versionId === selected?.id));
    const numbers = new Map<string, number>();
    all
      .filter((c) => !c.parentId && c.versionId === selected?.id)
      .forEach((c, i) => numbers.set(c.id, i + 1));
    return {
      top,
      numbers,
      replies: (id: string) => all.filter((c) => c.parentId === id),
      versionCount: all.filter((c) => !c.parentId && c.versionId === selected?.id).length,
      totalCount: all.filter((c) => !c.parentId).length,
    };
  }, [comments.data, scope, selected?.id]);

  if (banner.isLoading) return <Spinner />;
  if (banner.error || !data || !selected || !current) {
    return (
      <div className="page">
        <Notice kind="error">
          {banner.error ? errorMessage(banner.error) : 'Für dieses Motiv gibt es noch keine Datei.'}
        </Notice>
      </div>
    );
  }

  const perms = data.permissions;
  const { w, h } = sizeOf(selected);
  const columns = compare && previous ? 2 : 1;
  const availW = Math.max(80, (stageSize.width - 64 - (columns - 1) * 32) / columns);
  const availH = Math.max(80, stageSize.height - 64 - 30);
  const fitScale = Math.min(1, availW / w, availH / h);
  const scale = zoom === 'fit' ? fitScale : 1;
  const prevScale = previous ? Math.min(1, availW / sizeOf(previous).w, availH / sizeOf(previous).h) : 1;

  const pins = (comments.data ?? [])
    .filter((c) => !c.parentId && c.versionId === selected.id && c.pinX !== null && c.pinY !== null)
    .map((c) => ({
      id: c.id,
      number: thread.numbers.get(c.id) ?? 0,
      x: c.pinX!,
      y: c.pinY!,
      resolved: Boolean(c.resolvedAt),
    }));

  const siblingIndex = data.siblings.findIndex((s) => s.id === data.id);
  const prevSibling = data.siblings[siblingIndex - 1];
  const nextSibling = data.siblings[siblingIndex + 1];
  const decisionsForView = data.decisions
    .filter((d) => scope === 'all' || d.versionId === selected.id)
    .slice()
    .reverse();
  const latestDecision = data.decisions.find((d) => d.versionId === current.id);
  const isCurrent = selected.id === current.id;
  const clicks = events.filter((e) => e.type === 'click').map((e) => e.data.url ?? '');
  const errors = events.filter((e) => e.type === 'error');
  const checkFail = selected.checkSummary.fail;
  const checkWarn = selected.checkSummary.warn;

  type TimelineItem =
    { at: string; kind: 'comment'; comment: Comment } | { at: string; kind: 'decision'; decision: Decision };
  const timeline: TimelineItem[] = [
    ...thread.top.map((c) => ({ at: c.createdAt, kind: 'comment' as const, comment: c })),
    ...decisionsForView.map((d) => ({ at: d.createdAt, kind: 'decision' as const, decision: d })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  return (
    <div className="review">
      <div className="review-main">
        <div className="review-bar">
          <Link className="btn btn-ghost btn-sm" to={base} title="Zurück zur Kampagne">
            <ArrowLeft size={16} /> <span className="nowrap back-label">{data.campaign.name}</span>
          </Link>
          <div className="review-title grow">
            <span className="eyebrow">
              {formatName(data.width, data.height)}
              {data.width ? ` · ${data.width}×${data.height}` : ''} · {KIND_LABELS[data.kind]}
            </span>
            <h1 title={data.name}>{data.name}</h1>
          </div>
          <div className="versions" aria-label="Versionen">
            {data.versions.map((v) => (
              <button
                key={v.id}
                className="version-pill"
                aria-pressed={v.id === selected.id}
                onClick={() => setVersionId(v.id === current.id ? null : v.id)}
                title={`Version ${v.number} vom ${formatDateTime(v.createdAt)}`}
              >
                v{v.number}
              </button>
            ))}
          </div>
          <StatusChip status={data.status} />
          <div className="row">
            <button
              className="btn btn-ghost btn-sm btn-icon"
              disabled={!prevSibling}
              onClick={() => prevSibling && navigate(`${base}/motiv/${prevSibling.id}`)}
              aria-label="Vorheriges Motiv"
              title="Vorheriges Motiv (←)"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="small muted nowrap">
              {siblingIndex + 1} / {data.siblings.length}
            </span>
            <button
              className="btn btn-ghost btn-sm btn-icon"
              disabled={!nextSibling}
              onClick={() => nextSibling && navigate(`${base}/motiv/${nextSibling.id}`)}
              aria-label="Nächstes Motiv"
              title="Nächstes Motiv (→)"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="stage-toolbar">
          <button className="btn btn-outline btn-sm" onClick={replay} title="Neu abspielen (R)">
            <RotateCcw size={15} /> Neu abspielen
          </button>
          <div className="segmented" role="group" aria-label="Zoom">
            <button aria-pressed={zoom === 'fit'} onClick={() => setZoom('fit')}>
              Einpassen
            </button>
            <button aria-pressed={zoom === 'actual'} onClick={() => setZoom('actual')}>
              100 %
            </button>
          </div>
          <div className="swatches" role="group" aria-label="Hintergrund">
            {(Object.keys(BG_LABEL) as Bg[]).map((key) => (
              <button
                key={key}
                className={`swatch swatch-${key}`}
                aria-pressed={bg === key}
                aria-label={`Hintergrund ${BG_LABEL[key]}`}
                title={`Hintergrund: ${BG_LABEL[key]}`}
                onClick={() => setBg(key)}
              />
            ))}
          </div>
          {perms.canComment && (
            <button
              className={`btn btn-sm ${pinMode ? 'btn-accent' : 'btn-outline'}`}
              onClick={() => {
                setPinMode((m) => !m);
                setTab('feedback');
              }}
              title="Stelle im Banner markieren und kommentieren"
            >
              <MapPin size={15} /> {pinMode ? 'Stelle anklicken …' : 'Stelle markieren'}
            </button>
          )}
          {previous && (
            <button
              className={`btn btn-sm ${compare ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setCompare((c) => !c)}
            >
              <Columns2 size={15} /> Mit v{previous.number} vergleichen
            </button>
          )}
          <span className="spacer" />
          <a
            className="btn btn-ghost btn-sm btn-icon"
            href={selected.previewUrl}
            target="_blank"
            rel="noreferrer"
            title="In neuem Tab öffnen"
          >
            <ExternalLink size={16} />
          </a>
          {perms.canManage && (
            <>
              <button
                className="btn btn-primary btn-sm"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                <Upload size={15} /> {uploading ? 'Lädt …' : 'Neue Version'}
              </button>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept=".zip,.gif,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm"
                onChange={(e) => e.target.files?.[0] && void uploadVersion(e.target.files[0])}
              />
            </>
          )}
        </div>

        <div className={`stage bg-${bg}`} ref={stageRef}>
          {stageSize.width > 0 && compare && previous && (
            <StageColumn
              version={previous}
              scale={zoom === 'fit' ? prevScale : 1}
              replayKey={replayKey}
              label={`v${previous.number}`}
            />
          )}
          {stageSize.width > 0 && (
            <StageColumn
              version={selected}
              scale={scale}
              replayKey={replayKey}
              label={compare && previous ? `v${selected.number}` : undefined}
              pins={pins}
              draft={draft}
              pinMode={pinMode}
              highlight={highlight}
              onPlacePin={(pin) => {
                setDraft(pin);
                setPinMode(false);
                captureTime();
                setTimeout(() => textareaRef.current?.focus(), 0);
              }}
              onPinClick={(id) => {
                setTab('feedback');
                setHighlight(id);
              }}
              onEvent={onFrameEvent}
              onVideo={(el) => {
                videoEl.current = el;
              }}
            />
          )}
        </div>

        {(clicks.length > 0 || errors.length > 0) && (
          <div className="stage-events" aria-live="polite">
            {clicks.slice(-1).map((url, i) => (
              <span key={`c${i}`} className="row status-pass">
                <MousePointerClick size={15} /> Klick erkannt →{' '}
                <span style={{ wordBreak: 'break-all', color: 'var(--ink)' }}>{url || '(leere URL)'}</span>
              </span>
            ))}
            {errors.slice(-2).map((e, i) => (
              <span key={`e${i}`} className="row status-fail">
                <TriangleAlert size={15} /> JavaScript-Fehler im Banner: {e.data.message}
                {e.data.source ? ` (${e.data.source}:${e.data.line})` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      <aside className="review-side">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            {
              value: 'feedback',
              label: (
                <>
                  Feedback{' '}
                  {thread.versionCount > 0 && <span className="count-badge">{thread.versionCount}</span>}
                </>
              ),
            },
            {
              value: 'check',
              label: (
                <>
                  Technik{' '}
                  {checkFail ? (
                    <CheckIcon status="fail" size={15} />
                  ) : checkWarn ? (
                    <CheckIcon status="warn" size={15} />
                  ) : (
                    <CheckIcon status="pass" size={15} />
                  )}
                </>
              ),
            },
            { value: 'file', label: 'Datei' },
          ]}
        />

        <div className="side-scroll">
          {tab === 'feedback' && (
            <div className="thread">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div className="segmented" role="group" aria-label="Umfang">
                  <button aria-pressed={scope === 'version'} onClick={() => setScope('version')}>
                    Version {selected.number}
                  </button>
                  <button aria-pressed={scope === 'all'} onClick={() => setScope('all')}>
                    Alle ({thread.totalCount})
                  </button>
                </div>
              </div>
              {!isCurrent && (
                <Notice kind="warn">
                  Du siehst eine ältere Version.{' '}
                  <button className="text-link" onClick={() => setVersionId(null)}>
                    Zur aktuellen v{current.number}
                  </button>
                </Notice>
              )}
              {selected.note && <Notice>Notiz zur Version: {selected.note}</Notice>}
              {timeline.length === 0 && (
                <p className="small muted">
                  Noch kein Feedback.{' '}
                  {perms.canComment &&
                    'Schreib unten einen Kommentar oder markiere vorher eine Stelle im Banner.'}
                </p>
              )}
              {timeline.map((item) =>
                item.kind === 'decision' ? (
                  <DecisionEntry
                    key={item.decision.id}
                    decision={item.decision}
                    showVersion={scope === 'all'}
                  />
                ) : (
                  <CommentCard
                    key={item.comment.id}
                    comment={item.comment}
                    number={
                      item.comment.versionId === selected.id ? thread.numbers.get(item.comment.id) : undefined
                    }
                    replies={thread.replies(item.comment.id)}
                    canReply={perms.canComment}
                    canModerate={perms.canManage}
                    highlight={highlight === item.comment.id}
                    showVersion={scope === 'all'}
                    onFocus={() => setHighlight(item.comment.id)}
                    onReply={async (parent, body) => {
                      await sendComment.mutateAsync({ body, parentId: parent.id });
                    }}
                    onResolve={resolveComment}
                    onDelete={deleteComment}
                  />
                ),
              )}
            </div>
          )}
          {tab === 'check' && (
            <ChecksPanel version={selected} platforms={data.campaign.platforms} clicks={clicks} />
          )}
          {tab === 'file' && <FilePanel version={selected} banner={data} onDeleteVersion={deleteVersion} />}
        </div>

        {tab === 'feedback' && perms.canComment && (
          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) sendComment.mutate({ body: text.trim() });
            }}
          >
            {(draft || timeMs !== null) && (
              <div className="row-wrap small">
                {draft && (
                  <span className="chip chip-info">
                    <MapPin /> Stelle markiert
                    <button
                      type="button"
                      aria-label="Markierung entfernen"
                      style={{
                        border: 0,
                        background: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: 'inherit',
                      }}
                      onClick={() => setDraft(null)}
                    >
                      <X />
                    </button>
                  </span>
                )}
                {timeMs !== null && (
                  <span className="chip chip-outline">
                    bei {formatTimecode(timeMs)}
                    <button
                      type="button"
                      aria-label="Zeitpunkt entfernen"
                      style={{
                        border: 0,
                        background: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: 'inherit',
                      }}
                      onClick={() => setTimeMs(null)}
                    >
                      <X />
                    </button>
                  </span>
                )}
              </div>
            )}
            <textarea
              ref={textareaRef}
              className="textarea"
              placeholder={pinMode ? 'Klicke zuerst ins Banner …' : 'Feedback zu diesem Motiv …'}
              value={text}
              maxLength={5000}
              onFocus={() => timeMs === null && captureTime()}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim())
                  sendComment.mutate({ body: text.trim() });
              }}
            />
            <div className="row">
              {perms.canSeeInternal && (
                <label className="check small">
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />{' '}
                  Nur intern
                </label>
              )}
              <span className="spacer" />
              <button className="btn btn-primary btn-sm" disabled={!text.trim() || sendComment.isPending}>
                <Send size={15} /> Senden
              </button>
            </div>
          </form>
        )}

        {perms.canApprove && (
          <div className="decision-bar">
            {!isCurrent ? (
              <span className="small muted">
                Freigaben gelten für die aktuelle Version v{current.number}.
              </span>
            ) : data.status === 'approved' ? (
              <>
                <span className="grow small">
                  <strong className="status-pass">Freigegeben</strong>
                  {latestDecision && (
                    <span className="muted">
                      {' '}
                      von {latestDecision.actorName}, {relativeTime(latestDecision.createdAt)}
                    </span>
                  )}
                </span>
                {perms.canManage && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ flex: 'none' }}
                    onClick={() => decide.mutate({ decision: 'reset' })}
                  >
                    Zurücksetzen
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  className="btn btn-changes"
                  disabled={decide.isPending}
                  onClick={() => setChangesOpen(true)}
                >
                  Änderungen anfordern
                </button>
                <button
                  className="btn btn-approve"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ decision: 'approved' })}
                >
                  <Check size={17} strokeWidth={3} /> Freigeben
                </button>
              </>
            )}
          </div>
        )}
      </aside>

      <Dialog
        open={changesOpen}
        onClose={() => setChangesOpen(false)}
        title="Änderungen anfordern"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setChangesOpen(false)}>
              Abbrechen
            </button>
            <button
              className="btn btn-primary"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'changes_requested', note: changesNote.trim() })}
            >
              Änderungswunsch senden
            </button>
          </>
        }
      >
        <p className="muted">
          Was soll anders werden? Für Details direkt im Motiv kannst du zusätzlich Stellen markieren und
          kommentieren.
        </p>
        <textarea
          className="textarea"
          style={{ minHeight: 120 }}
          autoFocus
          value={changesNote}
          onChange={(e) => setChangesNote(e.target.value)}
          placeholder="z. B. Preis größer, Logo etwas weiter nach links, CTA in Gelb …"
        />
      </Dialog>
    </div>
  );
}
